// map-state.service.ts
import { computed, inject, Injectable, signal } from '@angular/core';
import { mapStyleMap, MapStyleType, MAPTILES_KEY, SatelliteCountryService } from '@wheere-front/shared-map-libre';
import { Map } from 'maplibre-gl';

/**
 * Shared map state for every tracker map (integrator, inside, customer). Reads the
 * maptiler key from the shared {@link MAPTILES_KEY} token — no app-specific environment
 * dep — so it can live in {@code domain-trackers} (scope:shared).
 *
 * Remplace aussi l'ancienne copie {@code inside-devices/util/map-state.service.ts}, dont
 * il reprend le pays satellite : l'écran qui charge un site/tracker déclare le pays une
 * fois (SatelliteCountryService) et toutes les cartes suivent — sans lui, un site hors
 * de France rendait une orthophoto blanche. `country` à null = fournisseur par défaut,
 * c'est-à-dire le comportement historique de l'integrator.
 */
@Injectable({
  providedIn: 'root',
})
export class MapStateService {
  map = signal<Map | undefined>(undefined);
  private maptilesKey = inject(MAPTILES_KEY);
  private readonly country = inject(SatelliteCountryService).country;

  mapStyleType = signal<MapStyleType>('street');
  mapStyle = computed(() => mapStyleMap(this.maptilesKey, this.country())[this.mapStyleType()]);

  public onToggleMapStyle() {
    this.mapStyleType.set(this.mapStyleType() === 'street' ? 'satellite' : 'street');
  }

  public removeLayers(layerIds: string[]) {
    const map = this.map();
    if (!map) return;

    layerIds.forEach(layerId => {
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
      if (map.getSource(layerId)) {
        map.removeSource(layerId);
      }
    });
  }

  public removeAllLayers(prefixes: string[]) {
    const map = this.map();
    if (!map) return;

    const layers = map.getStyle().layers || [];
    layers.forEach(layer => {
      if (prefixes.some(prefix => layer.id.startsWith(prefix))) {
        if (map.getLayer(layer.id)) {
          map.removeLayer(layer.id);
        }
        if (map.getSource(layer.id)) {
          map.removeSource(layer.id);
        }
      }
    });
  }

  flyTo(longitude: number, latitude: number) {
    this.map()?.flyTo({
      center: [longitude, latitude],
    });
  }
}
